import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkle, Copy, Check, CircleNotch } from "@phosphor-icons/react";

/**
 * One-tap AI "Explain this movement" — opens a modal, calls the explain endpoint,
 * shows a plain-English explanation with a Copy button. Explanations are cached
 * server-side so re-opening is instant.
 *
 * Props: endpoint (string, POST), body (object|null), title, subtitle, testid,
 *        size ("sm" pill | "md" button).
 */
export default function ExplainMovement({ endpoint, body = null, title = "Explain this movement", subtitle = "", testid = "explain", size = "sm" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [cached, setCached] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setOpen(true);
    if (text) return;
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, body || {});
      setText(data.text || "");
      setCached(!!data.cached);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "The AI explainer is unavailable right now.";
      toast.error(msg);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Explanation copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const btnClass = size === "md"
    ? "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-navy/[0.04] text-navy hover:bg-orange/10 hover:text-orangeink transition-colors"
    : "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy/70 hover:border-orange hover:text-orangeink transition-colors";

  return (
    <>
      <button onClick={run} data-testid={`${testid}-btn`} className={btnClass} title="AI: explain this in plain English">
        <Sparkle size={13} weight="fill" className="text-orangeink" /> Explain
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-lg rounded-2xl border-slate-200 p-0 gap-0" data-testid={`${testid}-dialog`}>
          <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-1.5 overline text-orangeink mb-1">
              <Sparkle size={13} weight="fill" /> AI explanation
            </div>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">{title}</DialogTitle>
            {subtitle && <DialogDescription className="text-navy/70">{subtitle}</DialogDescription>}
          </DialogHeader>

          <div className="px-7 py-6 min-h-[120px]">
            {loading ? (
              <div className="flex items-center gap-3 text-navy/65 text-sm py-6" data-testid={`${testid}-loading`}>
                <CircleNotch size={18} className="animate-spin text-orangeink" /> Lottie is reading the figures…
              </div>
            ) : (
              <p className="text-[0.95rem] leading-relaxed text-navy whitespace-pre-line" data-testid={`${testid}-text`}>{text}</p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 px-7 py-4 border-t border-slate-100">
            <span className="text-[0.68rem] text-slate-500">
              {cached ? "Cached — figures unchanged" : "Generated by Lottie AI"} · Review before sharing
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setOpen(false)} data-testid={`${testid}-close`}
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Close</button>
              <button onClick={copy} disabled={!text || loading} data-testid={`${testid}-copy`}
                className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-50">
                {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy</>}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
